import cx from "classnames";
import { Alert } from "components/common/Alert/Alert";
import { CustomSelect } from "components/CustomSelect/CustomSelect";
import { CustomDatePickerInput } from "components/DatePicker/CustomDatePickerInput";
import { useTranslationContext } from "context/translation";
import { LocalizationContext } from "context/localization";
import { isValidDate } from "helpers/date";
import { FC, useContext, useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { enUS, pl } from "date-fns/locale";
import { Field } from "react-final-form";
import { MachineRegistrationQuery } from "services/datocms/generated";

type MachineDetailsProps = {
  header?: string | null;
  locations?: NonNullable<
    MachineRegistrationQuery["machineRegistrationPage"]
  >["purchaseLocation"];
};

export const MachineDetails: FC<MachineDetailsProps> = ({
  locations,
  header,
}) => {
  const {
    placeOfPurchase = "Place of Purchase",
    dateOfPurchase = "Date of Purchase",
  } = useTranslationContext();
  const { minDate, maxDate } = useMemo(() => {
    const date = new Date();
    const minYear = date.getFullYear() - 2;
    date.setFullYear(minYear);
    return { minDate: date, maxDate: new Date() };
  }, []);
  const { selectLocationPlaceholder = "Select Location" } =
    useTranslationContext();
  const { localization } = useContext(LocalizationContext);

  const importedCalendarLocales = [pl, enUS];
  const currentLocale =
    importedCalendarLocales.find((l) => l.code === localization?.locale) ||
    enUS;

  return (
    <div className="bg-white p-10 rounded-[20px] border border-gray-200 flex flex-col gap-6">
      <h2 className="text-2xl font-semibold">{header}</h2>
      <div className="md:w-[400px]">
        <p className="text-xs font-medium mb-2">{`${placeOfPurchase}*`}</p>
        <Field
          name="purchaseLocation"
          render={({ input, meta }) => (
            <>
              <CustomSelect
                className={cx(
                  meta.touched && meta.error && "[&>div]:border-red-500",
                )}
                placeholder={selectLocationPlaceholder || ""}
                onChange={(event) => input.onChange(event?.value)}
                options={locations}
                name="purchaseLocation"
              />
              {meta.touched && meta.error && (
                <Alert hideBackground type="error">
                  {meta.error}
                </Alert>
              )}
            </>
          )}
        />
      </div>
      <div className="md:w-[400px] [&_.react-datepicker-wrapper]:w-full">
        <Field
          name="purchaseDate"
          render={({ input: { value, onChange } }) => (
            <DatePicker
              className="w-full"
              selected={value && isValidDate(value) ? new Date(value) : null}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              showPreviousMonths={false}
              onChange={onChange}
              minDate={minDate}
              maxDate={maxDate}
              customInput={
                <CustomDatePickerInput
                  inputName="purchaseDate"
                  label={`${dateOfPurchase}*` || ""}
                  localeCode={currentLocale.code}
                />
              }
              locale={currentLocale}
              excludeScrollbar
            />
          )}
        />
      </div>
    </div>
  );
};
